import {
  Box,
  Group,
  Text,
  Loader,
  Stack,
  Center,
  ScrollArea,
  ActionIcon,
  useMantineColorScheme,
  Button,
} from '@mantine/core';
import React from 'react';
import { FaBell, FaRegBellSlash } from 'react-icons/fa';

import { useNotifications } from '../../../hooks/useNotifications';

import { NotificationItem } from './NotificationItem';
import styles from './Notifications.module.css';

interface NotificationPanelProps {
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ onClose }) => {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const {
    notifications,
    isLoading,
    markAsRead,
    markAllAsRead,
    unreadCount,
    loadMore,
    hasMore,
    isFetchingMore,
    isMarkingAll,
  } = useNotifications();

  const handleMarkAsRead = async (id: string) => {
    const result = await markAsRead(id);
    if (result) {
      onClose();
    }
  };

  return (
    <Box w={350} style={{ overflow: 'hidden' }} className={styles.notificationPanel}>
      {/* Header */}
      <Box
        py="md"
        px="md"
        bg={isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-blue-6)'}
        c="var(--mantine-color-white)"
      >
        <Group gap="xs" justify="space-between">
          <Group gap="xs">
            <FaBell size={16} />
            <Text fw={600} size="md">
              Notifications
            </Text>
          </Group>
          {unreadCount > 0 && (
            <Button
              size="xs"
              variant="white"
              color={isDark ? 'dark' : 'blue'}
              onClick={async () => {
                await markAllAsRead();
              }}
              loading={isMarkingAll}
              style={{ fontWeight: 500 }}
            >
              Mark all as read
            </Button>
          )}
        </Group>
      </Box>

      {/* Content */}
      <ScrollArea.Autosize
        mah={400}
        scrollbarSize={8}
        type="always"
        style={{
          width: '100%',
          overflowX: 'hidden',
        }}
      >
        {isLoading ? (
          <Center py="xl">
            <Stack align="center" gap="xs">
              <Loader size="sm" />
              <Text size="sm">Loading notifications...</Text>
            </Stack>
          </Center>
        ) : !notifications || notifications.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="xs">
              <ActionIcon variant="subtle" color="gray" size="xl" radius="xl">
                <FaRegBellSlash size={20} />
              </ActionIcon>
              <Text size="sm">No notifications</Text>
              <Text size="xs" c="dimmed" ta="center" maw={250}>
                You'll see notifications here when someone mentions you in a comment
              </Text>
            </Stack>
          </Center>
        ) : (
          <Stack gap={0}>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={handleMarkAsRead}
                onClose={onClose}
              />
            ))}

            {isFetchingMore && (
              <Center py="sm">
                <Loader size="sm" />
              </Center>
            )}

            {hasMore && !isFetchingMore && (
              <Box p="xs" bg={isDark ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-gray-1)'}>
                <Text onClick={loadMore} size="sm" c="blue" ta="center" style={{ cursor: 'pointer' }}>
                  Load more
                </Text>
              </Box>
            )}
          </Stack>
        )}
      </ScrollArea.Autosize>
    </Box>
  );
};

export default NotificationPanel;
